import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { BRICK, Note, Panel, PlayButton, Title } from './deck';

/**
 * Reclamation's picker: ring up to `max` used emojis to get them back. The
 * play carries the picks, so nothing leaves the hand until "get them back".
 */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(38px, 1fr));
  gap: 4px;
  margin-top: 6px;
  max-height: 228px;
  overflow-y: auto;
`;

const Cell = styled.button`
  position: relative;
  height: 38px;
  border: 0;
  background: none;
  font-size: 22px;
  cursor: pointer;
  padding: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
`;

const Cancel = styled.button`
  border: 0;
  background: none;
  padding: 0 0 0 10px;
  font: inherit;
  font-size: 0.75rem;
  font-style: italic;
  color: #9ca3af;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
`;

interface ReclaimPickerProps {
  used: string[];
  max?: number;
  onDone: (picked: string[]) => void;
  /** Close without playing. */
  onCancel?: () => void;
  /** The play is on its way. */
  busy?: boolean;
  /** The used list is still loading. */
  loading?: boolean;
}

const ReclaimPicker: FC<ReclaimPickerProps> = ({ used, max = 10, onDone, onCancel, busy, loading }) => {
  const [picked, setPicked] = useState<string[]>([]);
  const toggle = (e: string) =>
    setPicked((p) => (p.includes(e) ? p.filter((x) => x !== e) : p.length >= max ? p : [...p, e]));
  return (
    <Panel style={{ padding: '14px 14px 12px' }}>
      <Title>♻️ pick the emojis you want back</Title>
      <Note>
        {loading ? 'reading your used emojis...' : `up to ${max}. you've used ${used.length} so far.`}
      </Note>
      <Grid>
        {used.map((e) => {
          const on = picked.includes(e);
          return (
            <Cell key={e} type="button" onClick={() => toggle(e)} aria-pressed={on} aria-label={on ? `${e}, picked` : e}>
              {on && (
                <motion.span
                  aria-hidden="true"
                  initial={{ scale: 0.6, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  style={{ position: 'absolute', inset: 1, borderRadius: '50%', border: `1.2px solid ${BRICK}`, background: 'rgba(180, 83, 42, 0.07)' }}
                />
              )}
              <span style={{ position: 'relative', opacity: !on && picked.length >= max ? 0.35 : 1 }}>{e}</span>
            </Cell>
          );
        })}
      </Grid>
      <Row>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <Note color={picked.length >= max ? BRICK : undefined} size={17}>
            {picked.length} / {max}
          </Note>
          {onCancel && (
            <Cancel type="button" onClick={onCancel} disabled={busy}>
              not now
            </Cancel>
          )}
        </div>
        <PlayButton type="button" disabled={picked.length === 0 || busy} onClick={() => onDone(picked)}>
          get them back
        </PlayButton>
      </Row>
    </Panel>
  );
};

export default ReclaimPicker;
